import moment from 'moment';
import Vue from 'vue';

moment.locale(Vue.$config.locale);

export default class DateTimeUtils {

  /**
   * Adds a specified number of days to a given date.
   *
   * @param {Date|string|number} targetDate - The target date to which days will be added.
   * @param {number} days - The number of days to add.
   * @returns {Date} - The new date after adding the specified number of days.
   */
  static addDays(targetDate, days) {
    if (!targetDate) {
      return null;
    }
    const d = moment(targetDate).add('days', days);
    return d.toDate();
  }

  /**
   * Calculates the difference between two dates in the specified unit of time.
   *
   * @param {Date|string|number} date1 - The first date.
   * @param {Date|string|number} date2 - The second date.
   * @param {string} [unitOfTime='days'] - The unit of time for the difference (e.g., 'days', 'hours', 'minutes').
   * @returns {number|null} - The difference between the two dates in the specified unit of time.
   */
  static diff(date1, date2, unitOfTime) {
    if (!date1 || !date2) {
      return null;
    }
    const d1 = moment(date1);
    const d2 = moment(date2);
    return d1.diff(d2, unitOfTime || 'days');
  }

  /**
   * Calculates the difference between two dates in days.
   *
   * @param {Date|string|number} date1 - The first date.
   * @param {Date|string|number} date2 - The second date.
   * @returns {number|null} - The difference between the two dates in days.
   */
  static dateDiff(date1, date2) {
    if (!date1 || !date2) {
      return null;
    }
    const d1 = moment(date1);
    const d2 = moment(date2);
    return d1.diff(d2, 'days');
  }

  /**
   * Calculates the difference between two dates in hours.
   *
   * @param {Date|string|number} date1 - The first date.
   * @param {Date|string|number} date2 - The second date.
   * @returns {number|null} - The difference between the two dates in hours.
   */
  static hourDiff(date1, date2) {
    if (!date1 || !date2) {
      return null;
    }
    const d1 = moment(date1);
    const d2 = moment(date2);
    return d1.diff(d2, 'hours');
  }

  /**
   * Parses a date string according to the specified pattern.
   *
   * @param {string} dateString - The date string to parse.
   * @param {string} [pattern='YYYY-MM-DD'] - The pattern to use for parsing the date string.
   * @returns {moment.Moment} - The parsed moment object.
   */
  static parseDate(dateString, pattern) {
    if (!dateString) {
      return null;
    }
    return moment(dateString, pattern || 'YYYY-MM-DD');
  }

  /**
   * Formats a date according to the specified pattern.
   *
   * @param {Date|string|number} value - The date to format.
   * @param {string} [pattern=Vue.$config.format.date] - The pattern to use for formatting the date.
   * @returns {string|null} - The formatted date string or null if the value is not provided.
   */
  static formatDate(value, pattern) {
    if (!value) {
      return null;
    }
    return moment(value).format(pattern || Vue.$config.format.date);
  }

  /**
   * Formats a date and time according to the specified pattern.
   *
   * @param {Date|string|number} value - The date and time to format.
   * @param {string} [pattern=Vue.$config.format.datetime] - The pattern to use for formatting the date and time.
   * @returns {string} - The formatted date and time string or an empty string if the value is not provided.
   */
  static formatDateTime(value, pattern) {
    if (!value) {
      return '';
    }
    return moment(value).format(pattern || Vue.$config.format.datetime);
  }

  /**
   * Formats a time according to the specified pattern.
   *
   * @param {Date|string|number} value - The time to format.
   * @param {string} [pattern=Vue.$config.format.time] - The pattern to use for formatting the time.
   * @returns {string} - The formatted time string or an empty string if the value is not provided.
   */
  static formatTime(value, pattern) {
    if (!value) {
      return '';
    }
    return moment(value).format(pattern || Vue.$config.format.time);
  }

  /**
   * Formats a year according to the specified pattern.
   *
   * @param {Date|string|number} value - The year to format.
   * @param {string} [pattern=Vue.$config.format.year] - The pattern to use for formatting the year.
   * @returns {string} - The formatted year string or an empty string if the value is not provided.
   */
  static formatYear(value, pattern) {
    if (!value) {
      return '';
    }
    return moment(value).format(pattern || Vue.$config.format.year);
  }

  /**
   * Gets the current date and time formatted according to the specified pattern.
   *
   * @param {string} [pattern] - The pattern to use for formatting the current date and time.
   * @returns {string} - The formatted current date and time string.
   */
  static now(pattern) {
    return moment().format(pattern);
  }
}

Vue.filter('formatDate', (value, pattern) => DateTimeUtils.formatDate(value, pattern));
Vue.filter('formatDateTime', (value, pattern) => DateTimeUtils.formatDateTime(value, pattern));
Vue.filter('formatTime', (value, pattern) => DateTimeUtils.formatTime(value, pattern));
Vue.filter('formatYear', (value, pattern) => DateTimeUtils.formatYear(value, pattern));



// WEBPACK FOOTER //
// ./src/utils/datetime.js